import { Helmet } from "react-helmet";
import { useTranslation } from "../i18n/LanguageContext";

const SITE_NAME = "Bibliona";

const SITE_URLS = {
  fr: "https://fr.bibliona.com",
  en: "https://en.bibliona.com",
};

export default function SEO({
  title,
  description,
  path = "",
  ogImage,
  noindex = false,
  jsonLd,
}) {
  const { lang, t } = useTranslation();

  const siteUrl = SITE_URLS[lang] || SITE_URLS.en;
  const defaultOgImage = `${siteUrl}/img/og-image.png`;
  const resolvedDescription = description || t("seo.defaultDescription");
  const resolvedOgImage = ogImage || defaultOgImage;

  const pageTitle = title
    ? `${title} | ${SITE_NAME}`
    : t("seo.defaultTitle");
  const canonicalUrl = `${siteUrl}${path}`;
  const alternateLang = lang === "fr" ? "en" : "fr";
  const alternateUrl = `${SITE_URLS[alternateLang]}${path}`;
  const ogLocale = lang === "fr" ? "fr_FR" : "en_US";

  return (
    <Helmet>
      <html lang={lang} />
      <title>{pageTitle}</title>
      <meta name="description" content={resolvedDescription} />
      <link rel="canonical" href={canonicalUrl} />
      <link rel="alternate" hrefLang={lang} href={canonicalUrl} />
      <link rel="alternate" hrefLang={alternateLang} href={alternateUrl} />
      <link rel="alternate" hrefLang="x-default" href={alternateUrl} />

      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={resolvedDescription} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={resolvedOgImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content={ogLocale} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={resolvedDescription} />
      <meta name="twitter:image" content={resolvedOgImage} />

      {/* JSON-LD Structured Data */}
      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
}
